import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Zap, Gift } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstructorPricingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // Check if user has referral discount available
  const hasReferralDiscount = user?.referred_by && !user?.referral_discount_used;
  const discountPercent = 20;

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 9.99,
      period: '/month',
      description: 'Perfect for getting started',
      courseLimit: 5,
      features: [
        'List up to 5 courses',
        'Unlimited students',
        'Video, text & quiz content',
        'Basic analytics',
        'Email support'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 29,
      period: '/month',
      description: 'For growing instructors',
      courseLimit: 15,
      features: [
        'List up to 15 courses',
        'Unlimited students',
        'AI course description generator',
        'Advanced analytics',
        'Priority support',
        'Featured in marketplace'
      ],
      popular: true
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      price: 59,
      period: '/month',
      description: 'For professional educators',
      courseLimit: 999,
      features: [
        'Unlimited courses',
        'Unlimited students',
        'All Pro features',
        'Custom branding',
        'Dedicated support',
        'Early access to new features',
        'API access'
      ],
      popular: false
    }
  ];

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.role !== 'instructor') {
      toast.error('Please register as an instructor to subscribe');
      return;
    }

    try {
      const response = await axios.post(`${API}/checkout/instructor-plan`, {
        plan: planId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const currentPlan = user?.instructor_plan;
  const isActive = user?.instructor_subscription_status === 'active';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="instructor-pricing-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-secondary text-secondary-foreground">For Instructors</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Start Teaching Today
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose a plan to list your courses. You keep 80% of every sale - 
              we only take a 20% platform fee.
            </p>
          </div>

          {/* Referral Discount Banner */}
          {hasReferralDiscount && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">Referral Discount Available!</span>
              </div>
              <p className="text-green-700">
                You have a <strong>{discountPercent}% discount</strong> on your first subscription. Applied automatically at checkout!
              </p>
            </div>
          )}

          {/* Commission Info */}
          <div className="bg-gradient-to-r from-primary/10 to-purple-100 rounded-xl p-6 mb-12 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-primary" />
              <span className="font-semibold text-slate-900">You keep 80% of every sale</span>
            </div>
            <p className="text-slate-600">
              Set your own course prices. Students pay per course. You earn with every sale.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const discountedPrice = hasReferralDiscount 
                ? (plan.price * (1 - discountPercent / 100)).toFixed(2) 
                : null;
              
              return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden border-2 ${
                  plan.popular ? 'border-primary shadow-xl shadow-indigo-500/10' : 'border-slate-200'
                }`}
                data-testid={`instructor-plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0">
                    <Badge className="rounded-none rounded-bl-lg bg-primary text-white">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-0">
                  <CardTitle className="font-heading text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="text-center mb-6">
                    {hasReferralDiscount ? (
                      <>
                        <span className="text-lg text-slate-400 line-through mr-2">${plan.price}</span>
                        <span className="font-heading text-5xl font-bold text-green-600">
                          ${discountedPrice}
                        </span>
                        <span className="text-slate-600">{plan.period}</span>
                        <p className="text-sm text-green-600 mt-1">{discountPercent}% referral discount!</p>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-5xl font-bold text-slate-900">
                          ${plan.price}
                        </span>
                        <span className="text-slate-600">{plan.period}</span>
                      </>
                    )}
                  </div>

                  <div className="text-center mb-6 p-3 bg-slate-50 rounded-lg">
                    <span className="text-2xl font-bold text-primary">
                      {plan.courseLimit === 999 ? '∞' : plan.courseLimit}
                    </span>
                    <span className="text-slate-600 ml-2">courses</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full h-12 ${
                      plan.popular 
                        ? 'bg-primary hover:bg-primary/90' 
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isActive && currentPlan === plan.id}
                    data-testid={`subscribe-instructor-btn-${plan.id}`}
                  >
                    {isActive && currentPlan === plan.id 
                      ? 'Current Plan' 
                      : `Start with ${plan.name}`}
                  </Button>
                </CardContent>
              </Card>
            )})}
          </div>

          {/* FAQ */}
          <div className="mt-16 text-center">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
              How it works
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mt-8 text-left">
              <div className="p-6 bg-white rounded-xl border border-slate-200">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="font-bold text-primary">1</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Subscribe to a plan</h3>
                <p className="text-slate-600 text-sm">Choose a plan based on how many courses you want to list.</p>
              </div>
              <div className="p-6 bg-white rounded-xl border border-slate-200">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="font-bold text-primary">2</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Create & price your courses</h3>
                <p className="text-slate-600 text-sm">Set your own prices. Add videos, text lessons, and quizzes.</p>
              </div>
              <div className="p-6 bg-white rounded-xl border border-slate-200">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="font-bold text-primary">3</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Earn 80% of every sale</h3>
                <p className="text-slate-600 text-sm">Students buy your courses. You keep 80%, we take 20%.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorPricingPage;
